import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ReportInfo } from '../../Interfaces/Reports/ReportInfo';
import { ApplicationState } from '../../store';
import { ReportCategory } from '../../Interfaces/Reports/ReportCategory';
import StringResources from '../../utils/stringResources';
import {
  PanelBar,
  PanelBarSelectEventArguments,
  PanelBarUtils,
  PanelBarItemProps,
} from '@progress/kendo-react-layout';
import { ReportTitleInfo } from '../../Interfaces/Reports/ReportTitleInfo';

const useStyles = makeStyles(theme =>
  createStyles({
    reportButtons: {
      margin: '10px',
      height: '70px',
    },
  })
);

interface Props {
  handleGenerateReportClick: (reportTitleInfo: ReportTitleInfo) => void;
  isReportsPanelBarVisible: boolean;
}

const ReportsList: React.FC<Props> = ({
  handleGenerateReportClick,
  isReportsPanelBarVisible,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const reportCategories: ReportCategory[] = useSelector(
    (state: ApplicationState) => state.reportsReducer.categories
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const [reportsPanelBarItem, setReportsPanelBarItem] = React.useState<
    PanelBarItemProps[]
  >([]);

  let components = PanelBarUtils.mapItemsToComponents(reportsPanelBarItem);
  const [expanded, setExpanded] = React.useState<string[]>([]);

  const [reportTitles, setReportTitles] = React.useState<ReportInfo[]>([]);

  const handleSelect = (event: PanelBarSelectEventArguments) => {
    if (event.expandedItems) {
      setExpanded(event.expandedItems);

      if (event.target.props.children === undefined) {
        const reportId = event.target.props.id;

        const reportInfo = reportTitles.filter(
          titles => titles.ReportID === reportId
        );

        const getTitles = reportInfo.map(title => {
          return title.ReportTitle;
        });

        const titles = new Map(Object.entries(getTitles[0]));

        const reportTitleInfo: ReportTitleInfo = {
          ReportID: reportId,
          ReportTitle: {
            English: titles.get('English'),
            French: titles.get('French'),
          },
        };

        if (reportTitleInfo !== undefined) {
          handleGenerateReportClick(reportTitleInfo);
        }
      }
    }
  };

  const getReportsInfoByCategories = () => {
    let reportsList: ReportInfo[] | undefined = [];
    let reportsPanelBarItem: PanelBarItemProps[] = [];

    for (let i = 0; i < reportCategories.length; ++i) {
      let reportPanelBarItem: PanelBarItemProps = {} as PanelBarItemProps;
      let reportsChildPanelBarItem: PanelBarItemProps[] = [];
      reportPanelBarItem.id = i.toString();
      reportPanelBarItem.title = reportCategories[i].CategoryName;
      reportPanelBarItem.expanded = true;

      if (reportsList !== undefined) {
        for (let r = 0; r < reportCategories[i].Reports.length; ++r) {
          let reportChildPanelBarItem: PanelBarItemProps =
            {} as PanelBarItemProps;
          reportChildPanelBarItem.id = reportCategories[i].Reports[r].ReportID;
          reportChildPanelBarItem.title = getReportTitle(
            reportCategories[i].Reports[r].ReportTitle
          );
          reportsChildPanelBarItem.push(reportChildPanelBarItem);
          setReportTitles(prevState => [
            ...prevState,
            reportCategories[i].Reports[r],
          ]);
        }
        reportPanelBarItem.children = reportsChildPanelBarItem;
      }
      reportsPanelBarItem.push(reportPanelBarItem);
    }

    setReportsPanelBarItem(reportsPanelBarItem);
  };

  const getReportTitle = (title: Map<string, string>) => {
    const titles = new Map(Object.entries(title));
    if (selectedLanguage === StringResources.English.ENGLISH_OPTION) {
      return titles.get('English');
    } else if (selectedLanguage === StringResources.English.FRENCH_OPTION) {
      return titles.get('French');
    }
  };

  React.useEffect(() => {
    if (reportCategories.length > 0) {
      getReportsInfoByCategories();
    }
  }, [reportCategories]);

  React.useEffect(() => {
    if (reportCategories.length > 0) {
      getReportsInfoByCategories();
    }
  }, [selectedLanguage]);

  return (
    <React.Fragment>
      {isReportsPanelBarVisible ? (
        <PanelBar
          expanded={expanded}
          children={components}
          onSelect={handleSelect}
        />
      ) : null}
    </React.Fragment>
  );
};

export default ReportsList;
